import { useEffect, useState } from 'react';
import { useUploadDocuments } from '../api/bookingService';

type DocumentFields = {
  dLFront?: string;
  dLBack?: string;
  passportFront?: string;
  passportBack?: string;
};

export const useCustomerDocumentUpload = (
  initialData?: Partial<DocumentFields>,
  onUploadSuccess?: (urls: DocumentFields) => void
) => {
  // Create initial state with defaults first, then override with passed initialData
  const [uploadedData, setUploadedData] = useState<DocumentFields>(
    initialData || {} // This will properly override the defaults
  );

  // Only update if initialData changes and is different from current state
  useEffect(() => {
    if (
      initialData &&
      JSON.stringify(initialData) !== JSON.stringify(uploadedData)
    ) {
      setUploadedData(initialData);
    }
  }, [JSON.stringify(initialData)]);

  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, File | null>
  >({});
  const [showWarning, setShowWarning] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>(
    {}
  );
  const [isUploading, setIsUploading] = useState(false);
  const uploadMutation = useUploadDocuments();

  const handleFileChange = (field: string, file: File | null) => {
    setSelectedFiles((prev) => ({ ...prev, [field]: file }));
    setValidationError(null); // Clear validation error when user makes a change
    setUploadError(null); // Clear upload error when user makes a change
    setFieldErrors((prev) => ({ ...prev, [field]: null })); // Clear field-specific error
  };

  const handleDeleteFile = (field: string) => {
    setUploadedData((prev) => ({ ...prev, [field]: '' }));
    setSelectedFiles((prev) => ({ ...prev, [field]: null }));
    setValidationError(null); // Clear validation error when user makes a change
    setUploadError(null); // Clear upload error when user makes a change
    setFieldErrors((prev) => ({ ...prev, [field]: null })); // Clear field-specific error
  };

  // Validate that both front and back documents are uploaded together
  const validateDocuments = (): { isValid: boolean; errorMessage?: string } => {
    // Clear previous field errors
    setFieldErrors({});

    // Check if only one side of DL is being uploaded
    const hasDLFront = !!selectedFiles.dLFront || !!uploadedData.dLFront;
    const hasDLBack = !!selectedFiles.dLBack || !!uploadedData.dLBack;

    if (hasDLFront && !hasDLBack) {
      const errorMessage =
        "Please upload both front and back of your driver's license";
      setFieldErrors((prev) => ({
        ...prev,
        dLBack: errorMessage,
      }));
      setValidationError(null); // Clear generic validation error
      return { isValid: false, errorMessage };
    }

    if (!hasDLFront && hasDLBack) {
      const errorMessage =
        "Please upload both front and back of your driver's license";
      setFieldErrors((prev) => ({
        ...prev,
        dLFront: errorMessage,
      }));
      setValidationError(null); // Clear generic validation error
      return { isValid: false, errorMessage };
    }

    // Check if only one side of passport is being uploaded
    const hasPassportFront =
      !!selectedFiles.passportFront || !!uploadedData.passportFront;
    const hasPassportBack =
      !!selectedFiles.passportBack || !!uploadedData.passportBack;

    if (hasPassportFront && !hasPassportBack) {
      const errorMessage =
        'Please upload both front and back of your passport/ID';
      setFieldErrors((prev) => ({
        ...prev,
        passportBack: errorMessage,
      }));
      setValidationError(null); // Clear generic validation error
      return { isValid: false, errorMessage };
    }

    if (!hasPassportFront && hasPassportBack) {
      const errorMessage =
        'Please upload both front and back of your passport/ID';
      setFieldErrors((prev) => ({
        ...prev,
        passportFront: errorMessage,
      }));
      setValidationError(null); // Clear generic validation error
      return { isValid: false, errorMessage };
    }

    return { isValid: true };
  };

  const handleUpload = async (includeExisting = true) => {
    const filesToUpload = Object.entries(selectedFiles).filter(
      ([, file]) => file
    );

    if (filesToUpload.length === 0 && !includeExisting) {
      setShowWarning(true);
      return { success: false, error: 'No files selected' };
    }

    // Validate documents before proceeding
    const validationResult = validateDocuments();
    if (!validationResult.isValid) {
      return { success: false, error: validationResult.errorMessage };
    }

    setShowWarning(false);
    setValidationError(null);
    setUploadError(null);
    setFieldErrors({}); // Clear all field errors
    setIsUploading(true);
    const updatedData = { ...uploadedData };

    try {
      // Upload new files first
      for (const [field, file] of filesToUpload) {
        if (!file) continue;
        try {
          const response = await uploadMutation.mutateAsync(file);
          // Replace with actual API call
          updatedData[field as keyof DocumentFields] =
            response.result as string;
        } catch (error: any) {
          // Set field-specific error
          let errorMessage =
            'We are facing an issue with uploading your document, you can provide document at counter';

          if (error?.response?.data?.message) {
            const apiMessage = error.response.data.message;
            // Check if it's a file size error and add specific information
            if (
              apiMessage.toLowerCase().includes('file size') ||
              apiMessage.toLowerCase().includes('size exceed')
            ) {
              errorMessage = `${apiMessage}. Maximum file size allowed is 2 MB.`;
            } else {
              // For all other API errors, show the user-friendly message
              errorMessage =
                'We are facing an issue with uploading your document, you can provide document at counter';
            }
          } else if (error?.message) {
            // Check for timeout errors specifically
            if (error.message.toLowerCase().includes('timeout')) {
              errorMessage =
                'We are facing an issue with uploading your document, you can provide document at counter';
            } else {
              // For all other error messages, show the user-friendly message
              errorMessage =
                'We are facing an issue with uploading your document, you can provide document at counter';
            }
          }

          setFieldErrors((prev) => ({ ...prev, [field]: errorMessage }));
          throw error; // Re-throw to be caught by outer try-catch
        }
      }

      setUploadedData(updatedData);
      setSelectedFiles({});

      // Return all URLs (both existing and newly uploaded)
      const urlsToReturn = includeExisting
        ? updatedData
        : Object.fromEntries(
            filesToUpload.map(([field]) => [
              field,
              updatedData[field as keyof DocumentFields],
            ])
          );

      onUploadSuccess?.(urlsToReturn as DocumentFields);

      return { success: true, data: urlsToReturn };
    } catch (error: any) {
      // Extract the specific error message from the API response
      let errorMessage =
        'We are facing an issue with uploading your document, you can provide document at counter';

      if (error?.response?.data?.message) {
        const apiMessage = error.response.data.message;
        // Check if it's a file size error and add specific information
        if (
          apiMessage.toLowerCase().includes('file size') ||
          apiMessage.toLowerCase().includes('size exceed')
        ) {
          errorMessage = `${apiMessage}. Maximum file size allowed is 2 MB.`;
        } else {
          // For all other API errors, show the user-friendly message
          errorMessage =
            'We are facing an issue with uploading your document, you can provide document at counter';
        }
      } else if (error?.message) {
        // Check for timeout errors specifically
        if (error.message.toLowerCase().includes('timeout')) {
          errorMessage =
            'We are facing an issue with uploading your document, you can provide document at counter';
        } else {
          // For all other error messages, show the user-friendly message
          errorMessage =
            'We are facing an issue with uploading your document, you can provide document at counter';
        }
      }

      setUploadError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadedData,
    selectedFiles,
    showWarning,
    validationError,
    uploadError,
    fieldErrors,
    handleFileChange,
    handleDeleteFile,
    handleUpload,
    uploadMutation,
    isUploading,
  };
};
